import CloseIcon from '@mui/icons-material/Close';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SwipeableDrawer,
  Typography,
} from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { withStyles } from '@mui/styles';
import { cloneDeep } from 'lodash';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';

import CloseCaptionModal from '../../modals/components/close-caption-modal/CloseCaptionModal';
import type { SaveAssetJob } from '../../store/slices/saveAssetJobsState/SaveAssetJob';
import { SaveAssetJobsSelector } from '../../store/slices/saveAssetJobsState/SaveAssetJobsState';

interface IProps {
  job: SaveAssetJob;
  index: number;
  closePopup: (job: SaveAssetJob, index) => void;
}

const StyledLinearProgress = withStyles({
  colorPrimary: {
    backgroundColor: 'gray',
  },
  barColorPrimary: {
    backgroundColor: 'green',
  },
})(LinearProgress);

const IngestUploadProgressPopup = forwardRef((props, ref) => {
  const { jobs, ingestInProgress } = useSelector(SaveAssetJobsSelector);
  // const [file, setFile] = useState(getIngestFile(job));
  const [openCloseCaptionModal, setOpenCloseCaptionModal] = useState(false);
  const [closeCaptionAssetForm, setCloseCaptionAssetForm] = useState(null);
  const [jobCount, setJobCount] = useState(0);

  const [state, setState] = React.useState({
    top: false,
    left: false,
    bottom: false,
    right: false,
  });
  type Anchor = 'top' | 'left' | 'bottom' | 'right';
  const toggleDrawer = (anchor: Anchor, open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    // console.log({ anchor, open });
    if (
      event &&
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setState({ ...state, [anchor]: open });
  };
  const showCloseCaptionModal = (assetForm) => {
    const form = cloneDeep(assetForm);
    // console.log('onCloseClick');
    setCloseCaptionAssetForm(form);
    setOpenCloseCaptionModal(true);
  };
  const getIngestFileName = (job: SaveAssetJob) => {
    let name = '';
    if (job.assetForm && job.assetForm.ingestFile) {
      name = job.assetForm.ingestFile.name;
    }
    if (job.channelForm && job.channelForm.ingestFile) {
      name = job.channelForm.ingestFile.name;
    }
    if (job.liveProgramForm && job.liveProgramForm.ingestFile) {
      name = job.liveProgramForm.ingestFile.name;
    }

    return name;
  };
  useImperativeHandle(ref, () => ({
    open: () => {
      setState({ ...state, right: !state.right });
    },
  }));
  useEffect(() => {
    const activeJobs = jobs.filter((j) => j.jobStatus !== 'Completed');
    const activeJobCount = activeJobs.length;

    const newJobsAdded = activeJobCount > jobCount;

    if (ingestInProgress && newJobsAdded) {
      setState((prev) => ({ ...prev, right: true }));
      setJobCount(activeJobCount);
    }

    // Update jobCount on any change
    if (activeJobCount !== jobCount) {
      setJobCount(activeJobCount);
    }

    // Optionally reset when no active jobs left
    if (!ingestInProgress && activeJobCount === 0) {
      setJobCount(0);
    }
  }, [ingestInProgress, jobs]);

  return ingestInProgress ? (
    <React.Fragment key="right">
      <SwipeableDrawer
        anchor="right"
        open={state.right}
        onClose={toggleDrawer('right', false)}
        onOpen={toggleDrawer('right', true)}
        className="ingest-progress-popup"
      >
        <Box
          sx={{ width: 350 }}
          role="presentation"
          onClick={toggleDrawer('right', false)}
          onKeyDown={toggleDrawer('right', false)}
        >
          <Typography textAlign="left" sx={{ margin: '10px', fontWeight: 'bold' }} variant="h5">
            Ingesting {jobs.length} assets
          </Typography>
          <IconButton
            aria-label="close"
            onClick={toggleDrawer('right', false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 0,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <List sx={{ borderTop: '1px solid #ddd' }}>
            {jobs.map((job, key) => (
              <ListItem key={key} disablePadding sx={{ borderBottom: '1px solid #ddd' }}>
                <ListItemButton sx={{ width: 330 }}>
                  <ListItemIcon>
                    <VideoFileIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box>
                        <Typography variant="body1">{getIngestFileName(job)}</Typography>
                        <Typography variant="body2">{job.message}</Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <StyledLinearProgress
                              variant="determinate"
                              value={job.progress}
                              sx={{ height: '8px', borderRadius: '10px', barColorPrimary: 'red' }}
                            />
                          </Box>
                          <Box sx={{ minWidth: 35 }}>
                            <span>{job.jobStatus === 'Pending' ? 'Pending' : `${Math.round(job.progress)}%`}</span>
                          </Box>
                        </Box>

                        <Box>
                          {job.showSubtitleLink && (
                            <div className="close-caption-section">
                              <span onClick={() => showCloseCaptionModal(job.assetForm)}>Upload closed captions file</span>
                            </div>
                          )}
                        </Box>
                      </Box>
                    }
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {openCloseCaptionModal && closeCaptionAssetForm && (
            <CloseCaptionModal
              open={openCloseCaptionModal}
              onClose={() => setOpenCloseCaptionModal(false)}
              assetForm={closeCaptionAssetForm}
            />
          )}
        </Box>
      </SwipeableDrawer>
    </React.Fragment>
  ) : (
    <></>
  );
});

export default IngestUploadProgressPopup;
